import {
  AppCard,
  Button,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiCheck } from "@mdi/js";
import { Alert, Box, Link } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { RnmRequestCreate } from "../../../../@types/services/RnmRequest";
import { createRnmRequest } from "../../../../__generated__/orval/endpoints/rnm-requests/rnm-requests";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import {
  formatLongTextExample,
  formatShortDescription,
} from "./-internal-components/formatText";
import { NewGeneralInformationForm } from "./-internal-components/NewGeneralInformationForm";
import { NewMaterialSection } from "./-internal-components/NewMaterialSection";
import {
  type NewRnmRequestSchema,
  newRnmRequestSchema,
} from "./-schemas/newRnmRequestSchema";

const NewRnmRequest: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const methods = useForm({
    defaultValues: {
      backup_requester: null,
      description: "",
      file_attachments: [],
      plant_field: null,
      plants: [],
      requester_area: null,
      rnm_forms: [
        {
          additional_information: "",
          bill_of_material_tag: "",
          certificate_group: null,
          certification: false,
          consumption_lot: undefined,
          criticality: null,
          estimated_time_pd: undefined,
          ibau: undefined,
          is_repetro: false,
          justification_pd: "",
          lead_time: undefined,
          manufacturer_name: null,
          manufacturer_name_free_text: "",
          material_category: null,
          material_group: null,
          maximum_stock: undefined,
          minimum_stock: undefined,
          model_type: "",
          moving_price: 0.01,
          mrp_type: null,
          ncm: null,
          norsok_name: null,
          old_matnumber: "",
          other_certificates: "",
          partnumber: "",
          purchase_group: null,
          purchase_order: "",
          quality_applicable_certificates: [],
          replace_material: false,
          return_document: "",
          stock_purchase: false,
          unit_measure: null,
          value_estimated: undefined,
          work_order: "",
        },
      ],
      subject: "",
    },
    resolver: zodResolver(newRnmRequestSchema),
  });

  const createRnmMutation = useMutation({
    mutationFn: (data: RnmRequestCreate) => createRnmRequest(data),
    onError: () => {
      enqueueSnackbar(t("rnmRequests.errors.createRequest"), {
        variant: "error",
      });
    },
    onSuccess: (data) => {
      enqueueSnackbar(
        <>
          {t("rnmRequests.messages.createSuccess")}
          <Box component="span" sx={{ marginLeft: 1 }}>
            <Link
              href={`/vbuyer/rnm_requests/${data.id}`}
              rel="noopener noreferrer"
              sx={{
                color: "inherit",
                fontFamily: "inherit",
                fontSize: "inherit",
                fontWeight: "inherit",
                textDecoration: "underline",
              }}
              target="_blank"
            >
              {t("common.viewRequest")}
            </Link>
          </Box>
        </>,
        { variant: "success" },
      );
      navigate({ to: "/vbuyer/mm_requests/create_new_requests" });
    },
  });

  const formatDataForSubmission = (
    data: NewRnmRequestSchema,
  ): RnmRequestCreate => {
    return {
      rnm_request: {
        mm_request_attributes: {
          backup_requester_id: data.backup_requester?.id,
          description: data.description,
          file_attachments: data.file_attachments || [],
          requester_area: data.requester_area.name,
          requester_field: data.plant_field.name,
          subject: data.subject,
        },
        plant_ids: data.plants.map((plant) => plant.id),
        rnm_forms_attributes: data.rnm_forms.map((form) => ({
          additional_attributes: form.additional_information?.trim()
            ? [{ key: "Addit.info", value: form.additional_information }]
            : [],
          additional_information: form.additional_information,
          bill_of_material_tag: form.bill_of_material_tag,
          certificate_group_id: form.certificate_group?.id,
          certification: form.certification ?? false,
          consumption_lot: form.consumption_lot,
          criticality: form.criticality ?? undefined,
          edit_po_text: "",
          estimated_time_pd: form.estimated_time_pd,
          ibau: form.ibau,
          is_repetro: form.is_repetro ?? false,
          is_serialized: form.is_serialized ?? false,
          justification_pd: form.justification_pd,
          lead_time: form.lead_time,
          long_text_example: formatLongTextExample({
            brandName: form.manufacturer_name?.name,
            modelType: form.model_type,
            norsokName: form.norsok_name?.name,
            otherCertificates: form.other_certificates,
            partnumber: form.partnumber,
            qualityApplicableCertificates: form.quality_applicable_certificates
              ? form.quality_applicable_certificates.map(
                  (cert) => cert?.name ?? "",
                )
              : [],
          }),
          manufacturer_name_free_text: form.manufacturer_name_free_text,
          manufacturer_name_id: form.manufacturer_name?.id,
          material_category_id: form.material_category.id,
          material_group_id: form.material_group.id,
          material_type_id: form.material_category.material_type.id,
          maximum_stock: form.maximum_stock,
          minimum_stock: form.minimum_stock,
          model_type: form.model_type,
          moving_price: form.moving_price,
          mrp_type: form.mrp_type ?? undefined,
          ncm_id: form.ncm?.id,
          norsok_name_id: form.norsok_name.id,
          old_matnumber: form.old_matnumber,
          other_certificates: form.other_certificates,
          partnumber: form.partnumber,
          purchase_group_id: form.purchase_group.id,
          purchase_order: form.purchase_order,
          quality_applicable_certificate_ids:
            form.quality_applicable_certificates?.map((cert) => cert.id) || [],
          replace_material: form.replace_material ?? false,
          return_document: form.return_document,
          serial_number: form.serial_number,
          short_description_en: formatShortDescription(
            form.norsok_name?.name,
            form.partnumber,
            form.manufacturer_name?.name,
          ),
          short_description_pt: formatShortDescription(
            form.norsok_name?.name,
            form.partnumber,
            form.manufacturer_name?.name,
          ),
          stock_purchase: form.stock_purchase ?? false,
          unit_measure_id: form.unit_measure.id,
          value_estimated: form.value_estimated,
          work_order: form.work_order,
        })),
        variant: "br",
      },
    };
  };

  const onSubmit: SubmitHandler<NewRnmRequestSchema> = (data) => {
    const formattedData = formatDataForSubmission(data);
    createRnmMutation.mutate(formattedData);
  };

  return (
    <PageContainer
      backTo="/vbuyer/mm_requests/create_new_requests"
      showBackButton
      title={t("common.rnmRequest")}
    >
      <FormProvider {...methods}>
        <form noValidate onSubmit={methods.handleSubmit(onSubmit)}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <AppCard title={t("common.generalInformation")}>
              <NewGeneralInformationForm />
            </AppCard>
            <NewMaterialSection />
            <AppCard>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  justifyContent:
                    Object.keys(methods.formState.errors).length > 0
                      ? "space-between"
                      : "flex-end",
                  width: "100%",
                }}
              >
                {Object.keys(methods.formState.errors).length > 0 && (
                  <Alert severity="error">
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {t("rnmRequests.errors.formValidation")}
                    </Box>
                  </Alert>
                )}
                <Button
                  color="success"
                  disabled={createRnmMutation.isPending}
                  loading={createRnmMutation.isPending}
                  startIcon={<Icon icon={mdiCheck} />}
                  type="submit"
                >
                  {t("common.submit")}
                </Button>
              </Box>
            </AppCard>
          </Box>
        </form>
      </FormProvider>
    </PageContainer>
  );
};

export const Route = createFileRoute("/_private/vbuyer/rnm_requests/new")({
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Create Material requests")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: NewRnmRequest,
});
